import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isMobileDevice = () =>
  window.matchMedia("(max-width: 900px)").matches ||
  /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(
    window.navigator.userAgent
  );

const isIOS = () =>
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

// Mismo patron que usa el interceptor de Axios en App.js para detectar la
// pantalla de login bajo HashRouter.
const isLoginRoute = () => /^#\/login(?:\?|$)/.test(window.location.hash);

export default function PwaInstallPrompt() {
  const theme = useTheme();
  const accentColor = theme.palette?.primary?.main || "#1976d2";

  const [deferredPrompt, setDeferredPrompt] = useState(
    window.deferredPwaPrompt || null
  );
  const [isVisible, setIsVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [onLoginRoute, setOnLoginRoute] = useState(isLoginRoute());
  const [siteLogo, setSiteLogo] = useState(null);

  // Logo del sitio actual: se lee del manifest.json que ya genera
  // scripts/generate-pwa-manifest.js por sitio, para no duplicar el mapeo
  // sitio -> icono en el bundle de React.
  useEffect(() => {
    let cancelled = false;

    fetch(`${process.env.PUBLIC_URL}/manifest.json`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.icons?.length) return;
        const icon =
          data.icons.find(
            (i) => i.sizes === "192x192" && i.purpose !== "maskable"
          ) || data.icons[0];
        if (icon?.src) {
          setSiteLogo(`${process.env.PUBLIC_URL}/${icon.src}`);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  // El componente esta montado una sola vez para toda la app (no se
  // desmonta al navegar), asi que hay que escuchar los cambios de hash para
  // saber cuando el usuario entra o sale de /login.
  useEffect(() => {
    const handleHashChange = () => setOnLoginRoute(isLoginRoute());
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    if (!onLoginRoute) {
      setIsVisible(false);
      return undefined;
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      window.deferredPwaPrompt = event;
      setDeferredPrompt(event);
      setIsVisible(isMobileDevice() && !isStandalone());
    };

    const handleInstallAvailable = () => {
      setDeferredPrompt(window.deferredPwaPrompt);
      setIsVisible(isMobileDevice() && !isStandalone());
    };

    const handleAppInstalled = () => {
      window.deferredPwaPrompt = null;
      setDeferredPrompt(null);
      setIsVisible(false);
    };

    if (window.deferredPwaPrompt && isMobileDevice() && !isStandalone()) {
      setDeferredPrompt(window.deferredPwaPrompt);
      setIsVisible(true);
    }

    // Chrome no siempre publica beforeinstallprompt inmediatamente. En ese
    // caso mantenemos una alternativa visible con instalación manual.
    const fallbackTimer = window.setTimeout(() => {
      if (isMobileDevice() && !isStandalone()) setIsVisible(true);
    }, 3000);

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("pwa-install-available", handleInstallAvailable);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.clearTimeout(fallbackTimer);
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener(
        "pwa-install-available",
        handleInstallAvailable
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [onLoginRoute]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    // Chromium permite usar cada evento beforeinstallprompt una sola vez.
    window.deferredPwaPrompt = null;
    setDeferredPrompt(null);
    setIsVisible(outcome !== "accepted");
    setShowInstructions(outcome !== "accepted");
  };

  if (!isVisible) return null;

  return (
    <aside
      className="pwa-install"
      aria-live="polite"
      style={{ "--pwa-accent": accentColor }}
    >
      <div className="pwa-install__content">
        {siteLogo && (
          <img
            src={siteLogo}
            alt=""
            className="pwa-install__logo"
            onError={() => setSiteLogo(null)}
          />
        )}
        <div className="pwa-install__copy">
          <strong>Instalar como app</strong>
          <span>Accedé rápidamente desde tu pantalla de inicio.</span>
        </div>
      </div>
      <div className="pwa-install__actions">
        {deferredPrompt ? (
          <button
            type="button"
            className="pwa-install__button pwa-install__button--primary"
            onClick={handleInstall}
          >
            Instalar
          </button>
        ) : (
          <button
            type="button"
            className="pwa-install__button pwa-install__button--primary"
            onClick={() => setShowInstructions((current) => !current)}
          >
            Cómo instalar
          </button>
        )}
        <button
          type="button"
          className="pwa-install__button pwa-install__button--dismiss"
          onClick={() => setIsVisible(false)}
          aria-label="Cerrar aviso de instalación"
        >
          Cerrar
        </button>
      </div>
      {showInstructions &&
        (isIOS() ? (
          <ol className="pwa-install__steps">
            <li className="pwa-install__step">
              <span className="pwa-install__step-icon" aria-hidden="true">
                <i className="fa fa-share-square"></i>
              </span>
              <span>
                Tocá el ícono <strong>Compartir</strong> en la barra del
                navegador (abajo en iPhone, arriba en iPad).
              </span>
            </li>
            <li className="pwa-install__step">
              <span className="pwa-install__step-icon" aria-hidden="true">
                <i className="fa fa-plus-square"></i>
              </span>
              <span>
                Buscá y elegí <strong>&quot;Agregar a Inicio&quot;</strong>.
              </span>
            </li>
          </ol>
        ) : (
          <p className="pwa-install__manual">
            Abrí el menú del navegador y elegí &quot;Instalar
            aplicación&quot; o &quot;Agregar a pantalla principal&quot;.
          </p>
        ))}
    </aside>
  );
}
